import React from "react";
import { NavLink, useNavigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useBranding } from "@/contexts/BrandingContext";
import { NAV, AUTH } from "@/constants/testIds";
import { Settings, LogOut, ArrowLeft } from "lucide-react";

const ROLE_LABELS = {
  super_admin: "Super Admin",
  bu_owner: "BU Owner",
  bu_manager: "BU Manager",
  user: "User",
};

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const { app_logo, app_name } = useBranding();
  const nav = useNavigate();
  const location = useLocation();

  const handleLogout = async () => {
    await logout();
    nav("/login");
  };

  // Universal back button: hide on the landing page + login flow; everywhere else,
  // use browser history (falls back to /business-units when there's nowhere to go).
  const hideBackOn = ["/business-units", "/login", "/change-password", "/"];
  const showBack = !hideBackOn.includes(location.pathname);
  const goBack = () => {
    // If we arrived here via a direct link (no history), send them home.
    if (window.history.length > 1) nav(-1);
    else nav("/business-units");
  };

  return (
    <div className="min-h-screen bg-zinc-50 flex flex-col" style={{ fontFamily: "'IBM Plex Sans', system-ui, sans-serif" }}>
      <header
        data-testid="app-header"
        className="sticky top-0 z-30 bg-white border-b border-zinc-200 flex items-center px-6 h-14 shrink-0 gap-6"
      >
        {/* Brand on the left */}
        <Link to="/business-units" className="flex items-center gap-2 shrink-0" data-testid="header-brand">
          {app_logo ? (
            <img src={app_logo} alt={app_name} className="max-h-8 max-w-[140px] object-contain" data-testid="header-app-logo" />
          ) : (
            <div
              className="text-base font-bold tracking-tight text-zinc-900"
              style={{ fontFamily: "'Cabinet Grotesk', 'IBM Plex Sans', sans-serif" }}
              data-testid="header-app-name"
            >
              {app_name}
            </div>
          )}
        </Link>

        {showBack && (
          <button
            type="button"
            onClick={goBack}
            data-testid="header-back-btn"
            title="Go back"
            className="text-xs text-zinc-600 hover:text-zinc-900 flex items-center gap-1 border border-zinc-200 rounded-sm h-8 px-2.5 hover:bg-zinc-50"
          >
            <ArrowLeft className="w-3.5 h-3.5" /> Back
          </button>
        )}

        {/* Right rail */}
        <div className="ml-auto flex items-center gap-3">
          <div className="text-right leading-tight hidden sm:block">
            <div className="text-sm font-medium text-zinc-900" data-testid="header-user-name">{user?.name}</div>
            <div className="text-[11px] text-zinc-500 truncate max-w-[220px]" data-testid="header-user-subtitle">
              {user?.title || user?.email}
            </div>
          </div>
          <span
            data-testid="header-user-role"
            className="text-[10px] uppercase tracking-wider px-2 py-0.5 bg-zinc-100 border border-zinc-200 rounded-sm text-zinc-700"
          >
            {ROLE_LABELS[user?.role] || user?.role}
          </span>
          {user?.role === "super_admin" && (
            <NavLink
              to="/settings"
              data-testid={NAV.settings}
              title="Settings"
              className={({ isActive }) =>
                `flex items-center justify-center w-9 h-9 rounded-sm border border-zinc-200 ${isActive ? "bg-zinc-900 text-white border-zinc-900" : "text-zinc-600 hover:bg-zinc-50"}`
              }
            >
              <Settings className="w-4 h-4" strokeWidth={1.75} />
            </NavLink>
          )}
          <button
            data-testid={AUTH.logoutButton}
            onClick={handleLogout}
            title="Log out"
            className="flex items-center gap-1.5 text-xs text-zinc-600 hover:text-zinc-900 border border-zinc-200 rounded-sm px-3 h-9 hover:bg-zinc-50"
          >
            <LogOut className="w-3.5 h-3.5" /> Log out
          </button>
        </div>
      </header>
      <main className="flex-1 overflow-x-hidden">
        <div className="px-10 py-8 max-w-[1600px] mx-auto">{children}</div>
      </main>
    </div>
  );
}
